import React, { useState, useEffect } from 'react';
import { X, Search, ChevronRight, User, AlertCircle, FileText, DollarSign, Trophy, Mail, Loader, ChevronDown, Plus } from 'lucide-react';
import { contactService, servicesService, sourcesService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUsers } from '../../hooks';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui/Dropdown';
import ProfileModal from './ProfileModal';

export default function NewTenderModal({ onClose, onSave, profiles, currentUser, onAddProfile, defaultProfile = null, defaultType = 'TENDER' }) {
  const { t } = useLanguage();
  const { users } = useUsers();
  const [step, setStep] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [searchedContacts, setSearchedContacts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [showContactCreator, setShowContactCreator] = useState(false);

  useEffect(() => {
    if (defaultProfile && !selectedContact) {
      setSelectedContact(defaultProfile);
      setStep(2); // Skip straight to the form
    }
  }, [defaultProfile, selectedContact]);

  useEffect(() => {
    let isMounted = true;
    servicesService.getAll().then(res => {
      if (isMounted) setAvailableServices(Array.isArray(res) ? res : []);
    }).catch(e => console.error(e));
    sourcesService.getAll().then(res => {
      if (isMounted) setAvailableSources(Array.isArray(res) ? res : []);
    }).catch(e => console.error(e));
    return () => { isMounted = false; };
  }, []);

  const [formData, setFormData] = useState({
    title: '',
    type: defaultType,
    assignedTo: currentUser ? currentUser.id : '',
    proposalValue: '',
    negotiation: '',
    grossProfit: '',
    gpPercent: '',
    wht: '',
    paymentTerms: '',
    mobilizationAdvance: '',
    contractDuration: '',
    noOfStaff: '',
    winningStrategy: '',
    notes: '',
    services: [],
    sourceId: '',
  });

  useEffect(() => {
    const pv = Number(formData.proposalValue) || 0;
    const gp = Number(formData.grossProfit) || 0;
    if (pv > 0 && gp >= 0) {
      setFormData(prev => ({ ...prev, gpPercent: ((gp / pv) * 100).toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, gpPercent: '' }));
    }
  }, [formData.proposalValue, formData.grossProfit]);

  useEffect(() => {
    if (!contactSearch || contactSearch.length < 2) { setSearchedContacts([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await contactService.getAll({ search: contactSearch, limit: 10 });
        setSearchedContacts(res.data || []);
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  const getContactName = (c) => {
    if (!c) return 'Unknown';
    if (c.clientName) return c.clientName;
    const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return full || c.name || 'Unknown';
  };

  const getContactPhone = (c) => {
    if (!c) return '';
    return c.mobile || c.phone || '';
  };

  const getContactInitial = (c) => {
    const name = getContactName(c);
    return name ? name[0].toUpperCase() : 'U';
  };

  const displayContacts = contactSearch ? searchedContacts : (profiles || []).slice(0, 5);

  const handleContactSelect = (profile) => {
    setSelectedContact(profile);
    setStep(2);
  };

  const handleCreateContact = async (data) => {
    const newContact = await onAddProfile(data);
    if (newContact) {
      setSelectedContact(newContact);
      setShowContactCreator(false);
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!selectedContact) {
      setLocalError(t('contactRequired') || 'Profile is required.');
      return;
    }
    setLocalError(null);
    setIsSaving(true);
    try {
      await onSave({
        profileId: selectedContact.id,
        prequalificationId: selectedContact.prequalificationId || null,
        title: formData.title || `${selectedContact.clientName || 'Client'} - Tender`,
        proposalValue: formData.proposalValue ? Number(formData.proposalValue) : null,
        negotiation: formData.negotiation ? Number(formData.negotiation) : null,
        grossProfit: formData.grossProfit ? Number(formData.grossProfit) : null,
        gpPercent: formData.gpPercent ? Number(formData.gpPercent) : null,
        wht: formData.wht ? Number(formData.wht) : null,
        paymentTerms: formData.paymentTerms || null,
        mobilizationAdvance: formData.mobilizationAdvance ? Number(formData.mobilizationAdvance) : null,
        contractDuration: formData.contractDuration || null,
        noOfStaff: formData.noOfStaff ? Number(formData.noOfStaff) : null,
        winningStrategy: formData.winningStrategy || null,
        notes: formData.notes || null,
        type: formData.type || 'TENDER',
        assignedTo: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        sourceId: formData.sourceId ? parseInt(formData.sourceId) : null,
        services: formData.services || [],
        status: 'In Process',
        awardStatus: 'In Process',
      });
      onClose();
    } catch (error) {
      console.error('Error creating tender:', error);
      setLocalError('Error creating tender');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {showContactCreator && (
        <ProfileModal
          onClose={() => setShowContactCreator(false)}
          onSave={handleCreateContact}
        />
      )}
      <div className="bg-white w-full max-w-2xl max-h-[95vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h3 className="font-bold text-gray-800 text-lg">
            {step === 1 ? (t('step1SelectProfile') || 'Step 1: Select Profile') : (t('step2TenderDetails') || 'Step 2: Tender Details')}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        
        {localError && (
          <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{localError}</span>
            <button onClick={() => setLocalError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="p-6 overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {t('leadProfileDescription') || 'Search and select an existing client profile for this tender.'}
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  className="w-full pl-10 p-3 border rounded-xl bg-gray-50 outline-none"
                  placeholder={t('searchByNameOrPhone') || 'Search by name or phone...'}
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {searching && <p className="text-center text-xs text-gray-500">{t('searching') || 'Searching...'}</p>}
                {displayContacts.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => handleContactSelect(c)}
                    className="flex items-center justify-between p-3 hover:bg-blue-50 rounded-lg cursor-pointer border border-transparent hover:border-blue-100 transition-all"
                  >
                    <div className="flex items-start gap-3 w-full overflow-hidden">
                      <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                        {getContactInitial(c)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-gray-900 text-sm truncate">
                            {c.clientName || getContactName(c)}
                          </p>
                          <div className="flex items-center gap-2">
                            {c.status && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 shrink-0">
                                {c.status}
                              </span>
                            )}
                            {getContactPhone(c) && (
                              <span className="text-xs font-medium text-gray-600 shrink-0 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                {getContactPhone(c)}
                              </span>
                            )}
                          </div>
                        </div>
                        {(c.contactPersonName || c.email) && (
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {c.contactPersonName && (
                              <div className="flex items-center gap-1.5 truncate">
                                <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="truncate">{c.contactPersonName}</span>
                              </div>
                            )}
                            {c.email && (
                              <div className="flex items-center gap-1.5 truncate">
                                <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="truncate">{c.email}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
                {!searching && displayContacts.length === 0 && contactSearch && (
                  <p className="text-center text-sm text-gray-400 py-2">{t('noContacts') || 'No contacts found.'}</p>
                )}
              </div>
              <button
                onClick={() => setShowContactCreator(true)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t('createNewProfile') || 'Create New Profile'}
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Selected Profile Banner */}
              <div className="bg-blue-50/80 border border-blue-100 p-3 rounded-xl flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-blue-900 text-sm">
                    {t('clientProfile') || 'Client Profile'}: {getContactName(selectedContact)}
                  </span>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold underline"
                >
                  {t('change') || 'Change'}
                </button>
              </div>

              {selectedContact?.prequalificationId && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-xl flex items-start gap-2 mt-3">
                  <AlertCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <p className="text-sm text-emerald-700 font-medium">
                    {t('activePrequalificationLinked') !== 'activePrequalificationLinked' ? t('activePrequalificationLinked') : 'This profile has an active prequalification. This tender will be automatically linked to it.'}
                  </p>
                </div>
              )}

              {/* Section 1: Basic Information */}
              <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3">
                <h4 className="font-bold text-xs text-blue-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-blue-200 pb-2">
                  <FileText className="w-4 h-4" /> {t('basicInformation') || '1. Basic Information'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3">
                    <label className="text-xs font-bold text-gray-600">{t('title') || 'Title'}</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder={`${getContactName(selectedContact)} - ${t('tender') || 'Tender'}`}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('type') || 'Type'}</label>
                    <Dropdown className="w-full mt-1">
                      <DropdownTrigger className="w-full">
                        <div className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none">
                          <span>{formData.type === 'PROPOSAL' ? (t('proposal') || 'Proposal') : (t('tender') || 'Tender')}</span>
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        </div>
                      </DropdownTrigger>
                      <DropdownPopover placement="bottom">
                        <DropdownMenu
                          selectedKeys={new Set([formData.type])}
                          onSelectionChange={(keys) => setFormData({ ...formData, type: Array.from(keys)[0] })}
                          selectionMode="single"
                        >
                          <DropdownItem id="TENDER">{t('tender') || 'Tender'}</DropdownItem>
                          <DropdownItem id="PROPOSAL">{t('proposal') || 'Proposal'}</DropdownItem>
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('assignee') || 'Assignee'}</label>
                    <Dropdown className="w-full mt-1">
                      <DropdownTrigger className="w-full">
                        <div className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none">
                          <span className="truncate">
                            {formData.assignedTo
                              ? (users?.find(u => u.id === formData.assignedTo)?.username || users?.find(u => u.id === formData.assignedTo)?.name || formData.assignedTo)
                              : (t('selectAssignee') || 'Select Assignee')}
                          </span>
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        </div>
                      </DropdownTrigger>
                      <DropdownPopover placement="bottom">
                        <DropdownMenu
                          selectedKeys={new Set([formData.assignedTo])}
                          onSelectionChange={(keys) => setFormData({ ...formData, assignedTo: Array.from(keys)[0] })}
                          selectionMode="single"
                        >
                          {users?.map(u => (
                            <DropdownItem key={u.id} id={u.id}>{u.username || u.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('source') || 'Source'}</label>
                    <Dropdown className="w-full mt-1">
                      <DropdownTrigger className="w-full">
                        <div className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none">
                          <span className="truncate">
                            {formData.sourceId
                              ? (availableSources?.find(s => s.id === formData.sourceId)?.name || formData.sourceId)
                              : (t('selectSource') || 'Select Source')}
                          </span>
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        </div>
                      </DropdownTrigger>
                      <DropdownPopover placement="bottom">
                        <DropdownMenu
                          selectedKeys={new Set([formData.sourceId])}
                          onSelectionChange={(keys) => setFormData({ ...formData, sourceId: Number(Array.from(keys)[0]) })}
                          selectionMode="single"
                        >
                          {availableSources?.map(s => (
                            <DropdownItem key={s.id} id={s.id}>{s.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>

                  <div className="sm:col-span-3">
                    <label className="text-xs font-bold text-gray-600">{t('services') || 'Services'}</label>
                    <Dropdown className="w-full mt-1">
                      <DropdownTrigger className="w-full">
                        <button
                          type="button"
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none"
                        >
                          <div className="flex-1 truncate text-left">
                            {formData.services?.length === 0 || !formData.services ? (
                              <span className="text-gray-400">{t('selectServices') || 'Select Services'}</span>
                            ) : formData.services?.length === 1 ? (
                              <span className="text-gray-900">{formData.services[0]}</span>
                            ) : (
                              <span className="text-blue-600 font-medium">
                                {formData.services.length} {t('selected') || 'Selected'}
                              </span>
                            )}
                          </div>
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover placement="bottom" className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set(formData.services || [])}
                          selectionMode="multiple"
                          onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
                        >
                          {(availableServices || []).map((s) => (
                            <DropdownItem key={s.name} id={s.name}>{s.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                </div>
              </div>

              {/* Section 2: Commercial Details */}
              <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/30 space-y-3">
                <h4 className="font-bold text-xs text-amber-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-amber-200 pb-2">
                  <DollarSign className="w-4 h-4" /> {t('commercialDetails') || '2. Commercial Details'}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('proposalValuePKR') || 'Proposal Value (PKR)'}</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white font-semibold text-blue-900"
                      value={formData.proposalValue}
                      onChange={(e) => setFormData({ ...formData, proposalValue: e.target.value })}
                      placeholder={t('eg500000') || "e.g. 500000"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('negotiationPKR') || 'Negotiated Amount (PKR)'}</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.negotiation}
                      onChange={(e) => setFormData({ ...formData, negotiation: e.target.value })}
                      placeholder={t('negotiationAmount') || "Negotiation amount"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('grossProfitPKR') || 'Gross Profit (PKR)'}</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.grossProfit}
                      onChange={(e) => setFormData({ ...formData, grossProfit: e.target.value })}
                      placeholder={t('eg50000') || "e.g. 50000"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('gpPercent') || 'GP %'}</label>
                    <input
                      readOnly
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-emerald-50 border-emerald-300 font-bold text-emerald-800 outline-none cursor-not-allowed"
                      value={formData.gpPercent}
                      placeholder={t('autoCalculatedPercent') || "Auto calculated %"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('whtWithholdingTax') || 'WHT (Withholding Tax)'}</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.wht}
                      onChange={(e) => setFormData({ ...formData, wht: e.target.value })}
                      placeholder={t('whtAmountOrPercent') || "WHT Amount or %"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('mobilizationAdvancePKR') || 'Mobilization Advance (PKR)'}</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.mobilizationAdvance}
                      onChange={(e) => setFormData({ ...formData, mobilizationAdvance: e.target.value })}
                      placeholder={t('advanceAmount') || "Advance amount"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('contractDuration') || 'Contract Duration'}</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.contractDuration}
                      onChange={(e) => setFormData({ ...formData, contractDuration: e.target.value })}
                      placeholder={t('egContractDuration') || "e.g. 1 Year, 6 Months"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('noOfStaff') || 'No. of Staff'}</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.noOfStaff}
                      onChange={(e) => setFormData({ ...formData, noOfStaff: e.target.value })}
                      placeholder={t('requiredStaffCount') || "Required staff count"}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600">{t('paymentTerms') || 'Payment Terms'}</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                      placeholder={t('egPaymentTerms') || "e.g. Net 30, 50% Advance"}
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Strategy & Notes */}
              <div className="border border-green-200 rounded-xl p-4 bg-green-50/30 space-y-3">
                <h4 className="font-bold text-xs text-green-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-green-200 pb-2">
                  <Trophy className="w-4 h-4" /> {t('strategyAndNotes') || '3. Strategy & Notes'}
                </h4>

                <div>
                  <label className="text-xs font-bold text-gray-600">{t('winningStrategy') || 'Winning Strategy'}</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                    value={formData.winningStrategy}
                    onChange={(e) => setFormData({ ...formData, winningStrategy: e.target.value })}
                    placeholder={t('keyStrategyPlaceholder') || "Key strategy to win this proposal..."}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-600">{t('notes') || 'Notes'}</label>
                  <textarea
                    className="w-full p-2 border rounded-lg mt-1 text-sm bg-white h-16 resize-none"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('additionalNotes') || "Additional notes..."}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2 shrink-0">
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
                disabled={isSaving}
              >
                {t('back') || 'Back'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-6 py-2 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 disabled:opacity-60"
              >
                {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : null}
                {isSaving ? (t('creating') || 'Creating...') : (t('createTender') || 'Create Tender')}
              </button>
            </>
          )}
          {step === 1 && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
            >
              {t('cancel') || 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
